import React, { useCallback, useEffect, useState } from "react";
import { useI18n } from "@core/i18n/i18n";
import { useObservable } from "micro-observables";
import styled from "@emotion/styled";
import { colors } from "@ui/style/colors";
import { TabPanel, TabView } from "primereact/tabview";
import { useHistory } from "@core/routing/use-history";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { patientProgramsManager, templateManager } from "@core/service-container";
import { physioMeTabletBreakpoint, stack } from "@ui/style";
import { LoadingContainer } from "@ui/components/common/loading-container";
import { NetworkError } from "@ui/components/common/error";
import { indexOfOrDefault } from "@utils/array-utils";
import { RoutesPath } from "@ui/navigation/routes";
import { useQueryParams } from "@core/routing/use-query-params";
import { EmptyTabs } from "@ui/pages/program/components/empty-tabs";
import { useProgramFilters } from "@ui/pages/program/hooks/use-program-filters";
import { useTemplateFilters } from "@ui/pages/program/hooks/use-template-filters";
import { ProgramsTab } from "@ui/pages/program/components/programs-tab";
import { TemplatesTab } from "@ui/pages/program/components/templates-tab";
import { EmptyContainer } from "@ui/pages/program/components/tabs.styles";

export enum ProgramListTabNames {
	Programs = "programs",
	Templates = "templates",
}

const tabIndices = [ProgramListTabNames.Programs, ProgramListTabNames.Templates];

export const Tabs: React.FC = () => {
	const { format } = useI18n();
	const { updateHistory } = useHistory<RoutesPath.ProgramList>();
	const { isMobile } = useDimensions();
	const { active } = useQueryParams<RoutesPath.ProgramList>();
	const [activeTab, setActiveTab] = useState(indexOfOrDefault(tabIndices, active, ProgramListTabNames.Programs));

	const programs = useObservable(patientProgramsManager.allPatientsPrograms);
	const { programTotalCount } = useProgramFilters(programs);

	const templates = useObservable(templateManager.templateItems) ?? [];
	const { templateTotalCount } = useTemplateFilters(templates);

	const listProgramsTemplates = useCallback(
		async () =>
			await Promise.all([patientProgramsManager.fetchAllPatientsPrograms(), templateManager.fetchAllTemplates()]),
		[],
	);

	const [fetchData, loading, errorLoading] = useAsyncOperation(listProgramsTemplates, {
		initialLoadingState: true,
	});

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const selectTab = (tabIndex: number) => {
		setActiveTab(tabIndex);
	};

	const headerStyle = { width: isMobile ? "100%" : "unset" };

	if (loading) {
		return <LoadingContainer />;
	}

	if (errorLoading) {
		return (
			<ErrorContainer>
				<NetworkError
					style={{ width: "100%", padding: "72px 32px" }}
					titleStyle={{ fontSize: "1.5rem" }}
					description={format("templateListing.error.description")}
					onRefresh={async () => {
						await fetchData();
					}}
				/>
			</ErrorContainer>
		);
	}

	if (programTotalCount === 0 && templateTotalCount === 0) {
		return (
			<EmptyContainer>
				<EmptyTabs />
			</EmptyContainer>
		);
	}

	return (
		<StyledTabView
			activeIndex={activeTab}
			onTabChange={e => {
				updateHistory({ active: tabIndices[e.index] });
				selectTab(e.index);
			}}
		>
			<TabPanel
				headerStyle={headerStyle}
				header={format("templateListing.programs.tabName", { count: programTotalCount })}
			>
				<ProgramsTab selectTab={tabName => selectTab(tabIndices.indexOf(tabName))} />
			</TabPanel>

			<TabPanel
				headerStyle={headerStyle}
				header={format("templateListing.templates.tabName", { count: templateTotalCount })}
			>
				<TemplatesTab />
			</TabPanel>
		</StyledTabView>
	);
};

const ErrorContainer = styled.div`
	${stack(undefined, "center", "stretch")};
	flex-grow: 1;
`;

const StyledTabView = styled(TabView)`
	.p-tabview-panels {
		padding: 0;
	}

	.p-tabview-nav {
		@media ${physioMeTabletBreakpoint} {
			background-color: ${colors.primaryBackground} !important;
		}
	}

	.p-tabview-nav-link {
		@media ${physioMeTabletBreakpoint} {
			background-color: ${colors.primaryBackground} !important;
			justify-content: center !important;
		}
	}
`;
